import { computed, onScopeDispose, ref } from 'vue'
import { defineStore } from 'pinia'
import api from '@/api'
import type { AuthAccount, AuthCallbackInput } from '@/types/auth'

/** Session identity follows auth events/local session, independently of profiles. */
export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthAccount | null>(null)
  const status = ref<'loading' | 'authenticated' | 'anonymous' | 'error'>('loading')
  const error = ref<string | null>(null)
  const initialized = ref(false)
  const changing = ref(false)
  let revision = 0
  let stop: (() => void) | null = null
  let read: Promise<void> | null = null

  function accept(account: AuthAccount | null) {
    revision++
    user.value = account
    status.value = account ? 'authenticated' : 'anonymous'
    initialized.value = true
    error.value = null
  }
  function subscribe() {
    if (!stop) stop = api.subscribeAuthSession(accept)
  }
  function refresh(): Promise<void> {
    if (read) return read
    const request = ++revision
    error.value = null
    if (!user.value) status.value = 'loading'
    const pending = (async () => {
      try {
        const account = await api.getAuthSession()
        if (request === revision) accept(account)
      } catch (cause) {
        if (request !== revision) return
        error.value = cause instanceof Error ? cause.message : 'Could not check your sign-in status.'
        status.value = user.value ? 'authenticated' : 'error'
        initialized.value = true
      }
    })()
    read = pending
    void pending.finally(() => { if (read === pending) read = null })
    return pending
  }
  async function initialize() {
    if (initialized.value && status.value !== 'error') return
    if (import.meta.env.VITE_DEV_MODE === 'true') {
      accept({ id: import.meta.env.VITE_DEV_USER_ID || 'dev-user-id', email: import.meta.env.VITE_DEV_USER_EMAIL || 'dev@storytellr.me' })
      return
    }
    try { subscribe() }
    catch (cause) {
      error.value = cause instanceof Error ? cause.message : 'Could not observe your sign-in status.'
      status.value = 'error'
      initialized.value = true
      return
    }
    await refresh()
  }
  async function establish(action: () => Promise<AuthAccount>) {
    if (changing.value) throw new Error('A sign-in change is already in progress.')
    subscribe()
    changing.value = true
    error.value = null
    const request = ++revision
    try {
      const account = await action()
      if (request === revision) accept(account)
      else if (user.value?.id !== account.id) throw new Error('The active account changed. Please sign in again.')
    } finally { changing.value = false }
  }
  async function logout() {
    if (changing.value) throw new Error('A sign-in change is already in progress.')
    changing.value = true
    const request = ++revision
    try {
      await api.signOut()
      if (request === revision) accept(null)
    } finally { changing.value = false }
  }
  onScopeDispose(() => { revision++; stop?.(); stop = null })
  return {
    user, status, error, initialized, changing,
    loading: computed(() => status.value === 'loading'),
    isAuthenticated: computed(() => user.value !== null),
    userId: computed(() => user.value?.id ?? null),
    email: computed(() => user.value?.email ?? null),
    initialize, refresh, logout,
    login: (email: string, password: string) => establish(() => api.signIn(email, password)),
    completeCallback: (input: AuthCallbackInput) => establish(() => api.completeAuthCallback(input)),
  }
})
